<template>
    <base-dialog :loading="loading" :value="dialog" :title="title" :width="520" :fullscreen="isMobile" @input="onInput" @close="hide">
        <skeleton-loader v-if="loading" type="table" />

        <template v-else>
            <div v-if="payments.length === 0" class="history-empty">{{ $t('business.no_payments') }}</div>

            <div v-else class="history-list">
                <button v-for="payment in payments" :key="payment.id" type="button" class="history-row"
                    @click="$emit('edit', payment)">
                    <span class="history-period">{{ periodLabel(payment.period) }}</span>
                    <span class="history-amount">{{ formatMoney(payment.amount) }}</span>
                    <span class="history-method">{{ methodLabel(payment.method) }}</span>
                    <span class="history-date">{{ dateLabel(payment.date) }}</span>
                </button>
            </div>

            <div v-if="payments.length > 0" class="history-total">
                <span>{{ $t('business.total_paid') }}</span>
                <strong>{{ formatMoney(total) }}</strong>
            </div>
        </template>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="$t('business.register_payment')" @confirm="$emit('create')" />
        </template>
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import { formatMoney, loadClientPayments, sumPayments } from "@/utils/Business";

export default {
    name: "PaymentsHistoryDialog",
    components: { BaseDialog, DialogActions, SkeletonLoader },
    props: {
        companyId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: true,
            client: null,
            payments: [],
        }
    },
    computed: {
        title(vm = this) {
            return vm.client?.name || vm.$t('business.payments')
        },
        total(vm = this) {
            return sumPayments(vm.payments)
        },
    },
    methods: {
        formatMoney,
        async show(client) {
            const vm = this
            vm.client = client
            vm.payments = []
            vm.dialog = true
            await vm.load()
        },
        async load() {
            const vm = this
            if (!vm.client?.id) {
                vm.loading = false
                return
            }
            vm.loading = true
            try {
                vm.payments = await loadClientPayments(vm.companyId, vm.client.id)
            } catch (error) {
                console.error('[business] loadClientPayments', error?.code, error?.message)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        periodLabel(period) {
            return this.$moment(period, 'YYYY-MM').format('MMMM YYYY')
        },
        dateLabel(date) {
            return date ? this.$moment(date, 'YYYY-MM-DD').format('DD/MM/YY') : this.$t('business.no_value')
        },
        methodLabel(method) {
            return method ? this.$t(`business.method_${method}`) : this.$t('business.no_value')
        },
    },
}
</script>

<style scoped>
.history-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.history-row {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1.2fr 0.8fr;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    text-align: left;
    font-size: var(--fs-label);
    color: var(--v-txtSecondary-base);
    transition: border-color 0.15s;
}

.history-row:hover {
    border-color: var(--v-outline2-base);
}

.history-period {
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    text-transform: capitalize;
}

.history-amount {
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.history-method,
.history-date {
    color: var(--v-txtMuted-base);
}

.history-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid var(--v-outline-base);
    font-size: var(--fs-label);
    color: var(--v-txtSecondary-base);
}

.history-empty {
    padding: 24px 0;
    text-align: center;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}
</style>
